import { zodResolver } from '@hookform/resolvers/zod';
import CT from '@marketplace-ai/tags';
import { FormProvider } from 'react-hook-form';
import z from 'zod';
import { ButtonFooter, FormLayout, FormRow, TextArea } from '~/components';
import { ERROR_MESSAGES } from '~/constants/errorMessages';
import { ExistingInquiryNotice } from '~/features/shared/market-content/ExistingInquiryNotice';
import { useCustomForm } from '~/hooks/useCustomForm';
import type { JobResponse } from '~/types/hlx/jobResponse';
import { useMsaasListingSearchRef } from '../../marketSelectors';
import { useSearchSide } from '../searchSide';

export const MessageModal = ({
  initialMessage,
  existingInquiry,
}: {
  initialMessage: string;
  existingInquiry?: JobResponse;
}) => {
  const { msaasListingsSearchRef } = useMsaasListingSearchRef();
  if (!msaasListingsSearchRef) return null;

  return (
    <MessageModalContent
      msaasListingsSearchRef={msaasListingsSearchRef}
      initialMessage={initialMessage}
      existingInquiry={existingInquiry}
    />
  );
};

type MsaasListingsSearchRef = NonNullable<
  ReturnType<typeof useMsaasListingSearchRef>['msaasListingsSearchRef']
>;

const MessageModalContent = ({
  msaasListingsSearchRef,
  initialMessage,
  existingInquiry,
}: {
  msaasListingsSearchRef: MsaasListingsSearchRef;
  initialMessage: string;
  existingInquiry?: JobResponse;
}) => {
  const side = useSearchSide();
  const methods = useCustomForm({
    resolver: zodResolver(z.object({ message: z.string().min(1, ERROR_MESSAGES.REQUIRED) })),
    defaultValues: {
      message: initialMessage,
    },
  });

  const onSubmitSuccess = (values: { message: string }) => {
    if (existingInquiry) return;
    msaasListingsSearchRef.send({
      type: 'ON_CONFIRM_MESSAGE',
      payload: { message: values.message, providerIds: [] },
    });
  };

  const onSubmitError = (err: unknown) => {
    console.error('Message submit error: ', err);
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(onSubmitSuccess, onSubmitError)}>
        <FormLayout>
          <h2 className="sub-heading-sb">Message</h2>
          <p>
            Sending an inquiry does not apply or submit an offer. Do not include names or contact
            information. Messages are screened while identities are masked; reaching engagement
            thresholds enables an unmask request, not automatic disclosure.
          </p>
          <FormRow>
            <TextArea name="message" className="h-40" />
          </FormRow>
        </FormLayout>
        {existingInquiry && (
          <ExistingInquiryNotice
            response={existingInquiry}
            // A Selling-tab message is a Sent response on the Selling tab, which a side-less link already means.
            side={side === 'OFFERING' ? 'buying' : undefined}
          />
        )}
        <ButtonFooter
          primaryButton={{
            label: 'Send',
            cyTag: CT.listingSearch.detailPanel.messageConfirmBtn,
            isSubmit: true,
            disabled: !!existingInquiry,
          }}
        />
      </form>
    </FormProvider>
  );
};
